import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { VStack, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Box, Text, Icon, Flex, Button } from '@chakra-ui/react';
import { arrayMove } from '@dnd-kit/sortable';
import DeliveryItem from './DeliveryItem';
import DeliveryDetail from './DeliveryDetail';
import DeliveryOrderEditor from './DeliveryOrderEditor';
import { FaTruck, FaSort } from 'react-icons/fa';
import { loadDeliveryOrder, saveDeliveryOrder, clearDeliveryOrder, applyDeliveryOrder } from '../utils/deliveryOrder';

const DeliveryList = ({ deliveries, deliveryDate, isLoadingMore = false, onDelivered, onDeliveryFailed, focusDeliveryId, focusNonce = 0 }) => {
  // 열린 항목은 배열 위치가 아니라 주문 id로 추적한다.
  // 아래 sortedDeliveries는 배송완료 여부에 따라 계속 재정렬되므로, 위치로
  // 기억하면 새로고침이나 완료 처리 뒤에 다른 주문의 상세가 열린 것처럼 보인다.
  const [expandedId, setExpandedId] = useState(null);
  const [deliveryState, setDeliveryState] = useState({});
  const [autoScrollTop, setAutoScrollTop] = useState(false);
  // 아이템 refs를 저장할 객체 생성 (주문 id를 키로 사용)
  const itemRefs = useRef({});
  // 기사님이 정한 배송 순서(delivery_id 배열). null이면 서버가 내려준 순서를 쓴다.
  const [customOrder, setCustomOrder] = useState(() => loadDeliveryOrder(deliveryDate));
  const [isEditingOrder, setIsEditingOrder] = useState(false);

  // 자정이 지나 배송예정일이 바뀌면 그날 저장해둔 순서로 다시 읽는다
  useEffect(() => {
    setCustomOrder(loadDeliveryOrder(deliveryDate));
  }, [deliveryDate]);

  // 아코디언 변경 핸들러 수정
  const handleAccordionChange = (index) => {
    const nextId = index === -1 ? null : sortedDeliveries[index]?.delivery_id ?? null;
    setExpandedId(nextId);

    // 아이템이 열렸을 때만 스크롤 처리
    if (nextId !== null) {
      // 약간의 지연을 두어 아코디언이 열리는 애니메이션이 시작된 후 스크롤
      setTimeout(() => {
        const itemElement = itemRefs.current[nextId];
        if (itemElement) {
          // 아이템 요소의 위치로 스크롤
          itemElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });

          const yOffset = -20;
          const y = itemElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100); // 100ms 지연
    }
  };

  useEffect(() => {
    if (autoScrollTop) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setAutoScrollTop(false);
      });
    }
  }, [autoScrollTop]);

  // QR로 진입한 배송묶음 자동 펼침.
  // 대상 묶음이 뒤쪽 페이지에 있으면 목록이 로딩되는 동안 deliveries가 계속
  // 늘어나므로, 찾을 때까지 지켜보다가 나타나는 순간 한 번만 펼친다.
  //
  // 처리 완료 여부를 "id + 스캔 회차"로 기억한다.
  // id만 보면 같은 배송을 다시 스캔했을 때(카드를 닫았다가 다시 열려고 할 때)
  // 이미 처리한 id로 판단해 아무 반응도 하지 않는다. QR 링크로 진입한 경우는
  // focusNonce가 0으로 고정이라 기존 동작 그대로다.
  const focusHandledKeyRef = useRef(null);

  useEffect(() => {
    const focusKey = `${focusDeliveryId}:${focusNonce}`;

    if (!focusDeliveryId || focusHandledKeyRef.current === focusKey) {
      return;
    }

    const target = deliveries.find(delivery => delivery.delivery_id === focusDeliveryId);
    if (!target) {
      return; // 아직 로딩되지 않았거나 오늘 목록에 없는 묶음
    }

    focusHandledKeyRef.current = focusKey;
    // 순서 편집 중에 QR을 스캔했으면 편집을 마치고 해당 카드를 연다
    setIsEditingOrder(false);
    setExpandedId(focusDeliveryId);

    // 아코디언이 열린 뒤 해당 카드로 스크롤
    setTimeout(() => {
      const itemElement = itemRefs.current[focusDeliveryId];
      if (itemElement) {
        itemElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);
  }, [deliveries, focusDeliveryId, focusNonce]);

  const handleDeliverySuccess = useCallback((orderId, imageUrl) => {
    setDeliveryState(prev => ({
      ...prev,
      [orderId]: { completed: true, imageUrl }
    }));

    if (onDelivered) {
      onDelivered(orderId);
    }

    setExpandedId(null);

    setTimeout(() => {
      setAutoScrollTop(true);
    }, 10);
  }, [onDelivered]);


  const orderedDeliveries = useMemo(
    () => applyDeliveryOrder(deliveries, customOrder),
    [deliveries, customOrder]
  );

  // 정한 순서 안에서도 배송완료 건은 맨 아래로 내린다
  const { pendingDeliveries, sortedDeliveries } = useMemo(() => {
    const pending = [];
    const completed = [];

    orderedDeliveries.forEach(delivery => {
      const isComplete =
        delivery.status_in_word === '배송완료' ||
        deliveryState[delivery.delivery_id]?.completed;

      if (isComplete) {
        completed.push(delivery);
      } else {
        pending.push(delivery);
      }
    });

    return { pendingDeliveries: pending, sortedDeliveries: [...pending, ...completed] };
  }, [orderedDeliveries, deliveryState]);

  // 편집 화면에는 미완료 건만 보이므로, 미완료 건이 차지하던 자리만 새 순서로 채운다.
  // 사이사이 완료 건의 위치는 그대로 두어야, 실패 처리로 다시 미완료가 된 건이
  // 원래 자리로 돌아온다.
  const reorderPending = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || toIndex < 0 || toIndex >= pendingDeliveries.length) {
      return;
    }

    const reordered = arrayMove(pendingDeliveries, fromIndex, toIndex);
    const pendingIds = new Set(pendingDeliveries.map(delivery => delivery.delivery_id));
    let nextPending = 0;
    const ids = orderedDeliveries.map(delivery =>
      pendingIds.has(delivery.delivery_id)
        ? reordered[nextPending++].delivery_id
        : delivery.delivery_id
    );

    setCustomOrder(ids);
    saveDeliveryOrder(deliveryDate, ids);
  };

  const resetOrder = () => {
    if (!window.confirm('처음 받은 순서로 되돌릴까요?')) {
      return;
    }
    clearDeliveryOrder();
    setCustomOrder(null);
  };

  // 열린 배송묶음이 목록에서 사라지면 findIndex가 -1을 돌려주어 자연스럽게 닫힌다
  const expandedIndex = sortedDeliveries.findIndex(item => item.delivery_id === expandedId);

  return (
    <VStack spacing={4} align="stretch">
      {deliveries.length === 0 ? (
        <Box textAlign="center" mt={10}>
          <Icon as={FaTruck} boxSize={16} color="#E9673D" />
          <Text mt={4} fontSize="lg" fontWeight="bold">
            금일은 배송이 없습니다.
          </Text>
        </Box>
      ) : (
        <>
        <Flex justify="flex-end" align="center" gap={2} px={4} pt={3}>
          {isEditingOrder ? (
            <>
              <Button size="sm" variant="ghost" onClick={resetOrder} isDisabled={!customOrder}>
                원래 순서로
              </Button>
              <Button size="sm" colorScheme="orange" onClick={() => setIsEditingOrder(false)}>
                완료
              </Button>
            </>
          ) : (
            pendingDeliveries.length > 1 && (
              <Button
                size="sm"
                variant="outline"
                colorScheme="orange"
                leftIcon={<FaSort />}
                onClick={() => setIsEditingOrder(true)}
                // 뒤 페이지를 받는 중에 저장하면 아직 안 온 배송묶음의 순서가 빠진다
                isDisabled={isLoadingMore}
              >
                순서 편집
              </Button>
            )
          )}
        </Flex>

        {isEditingOrder && (
          <DeliveryOrderEditor deliveries={pendingDeliveries} onMove={reorderPending} />
        )}

        {/* 편집 중에도 내리지 않고 숨기기만 한다. 열려 있던 카드의 촬영 사진이 사라지지 않게 */}
        <Box display={isEditingOrder ? 'none' : 'block'}>
        <Accordion
          allowToggle
          index={expandedIndex}
          onChange={handleAccordionChange}
          reduceMotion={true}
        >
          {sortedDeliveries.map((delivery_info) => {
            const isCompleted =
              delivery_info.status_in_word === '배송완료' ||
              deliveryState[delivery_info.delivery_id]?.completed;

            const displayImage =
              delivery_info.delivery_image?.image ||
              deliveryState[delivery_info.delivery_id]?.imageUrl;

            return (
              <AccordionItem
                key={delivery_info.delivery_id}
                // ref 추가 - 각 아이템에 고유 참조 생성 (재정렬돼도 유지되도록 id로 키를 잡는다)
                ref={el => itemRefs.current[delivery_info.delivery_id] = el}
                bg={isCompleted ? '#E4E4e1' : 'white'}
                borderRadius="md"
                overflow="hidden"
                boxShadow="sm"
                transition="background-color 0.1s ease"
              >

                <h2>
                  <AccordionButton
                    style={{
                      WebkitTapHighlightColor: 'transparent',
                      WebkitTouchCallout: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none',
                      userSelect: 'none',
                      outline: 'none'
                    }}
                    _active={{ bg: 'inherit !important' }}
                    _focus={{ boxShadow: 'none !important', outline: 'none !important' }}
                    _hover={{ bg: 'inherit' }}
                    sx={{
                      '&:active': { backgroundColor: 'inherit !important' },
                      '&:focus': { backgroundColor: 'inherit !important' }
                    }}
                  >
                    <Box flex="1" textAlign="left">
                      <DeliveryItem
                        b_nm={delivery_info.b_nm}
                        status={isCompleted ? '배송완료' : delivery_info.status_in_word}
                        orderCount={delivery_info.order_ids?.length}
                      />
                    </Box>
                    <AccordionIcon mt={8} />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={10}>
                  {expandedId === delivery_info.delivery_id && (
                    <DeliveryDetail
                      delivery_id={delivery_info.delivery_id}
                      image={displayImage}
                      phone={delivery_info.v_phone_num}
                      address={delivery_info.address}
                      content={delivery_info.memo}
                      password={delivery_info.gate_password}
                      note={delivery_info.note}
                      order_items={delivery_info.order_items}
                      onDelivered={(deliveryId, imageUrl) => handleDeliverySuccess(deliveryId, imageUrl)}
                      onDeliveryFailed={onDeliveryFailed}
                    />
                  )}
                </AccordionPanel>
              </AccordionItem>
            );
          })}
        </Accordion>
        </Box>
        </>
      )}
    </VStack>
  );
};

export default DeliveryList;